import React, { useState } from "react";
import { MdDelete } from "react-icons/md";
import { BsPlus } from "react-icons/bs";
import TextField from "../../../components/Input/TextField";
import { useLanguage } from "../../../contexts/LanguageContext";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { v4 as uuidv4 } from "uuid";
import EditableContent from "../../../components/Input/EditableContent";
import BossUpdatesInput from "./BossUpdatesInput";
import ImageInput from "../../../components/Input/ImageInput";
import Loading from "../../../components/Loading";

const IMG_URL = process.env.REACT_APP_IMG_URL;

const SortableSection = ({ id, title, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <div
      className="bg-gray-800 py-2 m-2 rounded-md cursor-grab text-md grid grid-cols-2 text-start px-12"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <span>{index + 1}. Bölüm</span>
      <span>{title || "Başlıksız Bölüm"}</span>
    </div>
  );
};

const PatchForm = ({
  setHasChange,
  patch,
  setPatch,
  onSave,
  onDelete,
  loading,
  setLoading,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { language } = useLanguage();
  const sensors = useSensors(useSensor(PointerSensor));

  const handlePatchChange = (field, value) => {
    setPatch({ ...patch, [field]: value });
    setHasChange && setHasChange(true);
  };

  const handleSectionChange = (index, field, value) => {
    const newSections = [...patch.sections];
    newSections[index][field] = value;
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  // TABLO işlemleri Başlangıç
  const addTable = (sectionIndex) => {
    const newSections = [...patch.sections];
    newSections[sectionIndex].table = { columns: [""], rows: [[""]] };
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const deleteTable = (sectionIndex) => {
    const newSections = [...patch.sections];
    newSections[sectionIndex].table = { columns: [], rows: [] };
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const handleColumnChange = (sectionIndex, colIndex, value) => {
    const newSections = [...patch.sections];
    newSections[sectionIndex].table.columns[colIndex] = value;
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const handleRowChange = (sectionIndex, rowIndex, colIndex, value) => {
    const newSections = [...patch.sections];
    newSections[sectionIndex].table.rows[rowIndex][colIndex] = value;
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const addColumn = (sectionIndex) => {
    const newSections = [...patch.sections];
    newSections[sectionIndex].table.columns.push("");
    newSections[sectionIndex].table.rows = newSections[
      sectionIndex
    ].table.rows.map((r) => [...r, ""]);
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const removeColumn = (sectionIndex, colIndex) => {
    const newSections = [...patch.sections];
    newSections[sectionIndex].table.columns.splice(colIndex, 1);
    newSections[sectionIndex].table.rows = newSections[
      sectionIndex
    ].table.rows.map((r) => {
      const row = [...r];
      row.splice(colIndex, 1);
      return row;
    });
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const addRow = (sectionIndex) => {
    const newSections = [...patch.sections];
    const colCount = newSections[sectionIndex].table.columns.length;
    newSections[sectionIndex].table.rows.push(new Array(colCount).fill(""));
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const removeRow = (sectionIndex, rowIndex) => {
    const newSections = [...patch.sections];
    newSections[sectionIndex].table.rows.splice(rowIndex, 1);
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };
  // TABLO işlemleri Sonu

  // Foto yükleme
  const handleImageUpload = (e, index, field = "image") => {
    const file = e.target.files[0];
    const reader = new FileReader();
    const previewUrl = URL.createObjectURL(file);

    reader.onloadend = () => {
      if (field === "image") {
        setPatch({ ...patch, image: previewUrl, imageFile: file });
        setHasChange && setHasChange(true);
      } else if (field === "sections") {
        const newSections = [...patch.sections];
        newSections[index].images[0] = previewUrl;
        newSections[index].imageFile = file;
        setPatch({ ...patch, sections: newSections });
        setHasChange && setHasChange(true);
      } else if (field === "thumbnail") {
        const newSections = [...patch.sections];
        newSections[index].thumbnail[0] = previewUrl;
        newSections[index].thumbnailFile = file;
        setPatch({ ...patch, sections: newSections });
        setHasChange && setHasChange(true);
      }
    };

    if (file) reader.readAsDataURL(file);
  };

  // Bölüm ekle - sil
  const addSection = (bossUpdate = false) => {
    setPatch({
      ...patch,
      sections: [
        ...patch.sections,
        {
          id: uuidv4(),
          title: "",
          content: "",
          images: [],
          thumbnail: [],
          table: { columns: [], rows: [] },
          bossUpdate: bossUpdate,
        },
      ],
    });
    setHasChange && setHasChange(true);
  };

  const removeSection = (index) => {
    const newSections = [...patch.sections];
    newSections.splice(index, 1);
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = patch.sections.findIndex((s) => s.id === active.id);
    const newIndex = patch.sections.findIndex((s) => s.id === over.id);

    const newSections = arrayMove([...patch.sections], oldIndex, newIndex);
    setPatch({ ...patch, sections: newSections });
    setHasChange && setHasChange(true);
  };

  if (!patch) return <Loading />

  return (
    <div className="w-full mx-auto p-4 mt-12 mb-24">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave(patch);
        }}
      >
        <h1 className="text-3xl font-bold mb-4">
          Yama Formu <span className="uppercase">{language}</span>
        </h1>

        <TextField
          placeholder="Yama Versiyonu"
          label="Yama Versiyonu"
          value={patch.yamaVersiyonu}
          onChange={(e) => handlePatchChange("yamaVersiyonu", e.target.value)}
          className="w-full p-2 mb-1 border"
          required
        />
        <TextField
          placeholder="Yayın Tarihi"
          type="date"
          label="Yayın Tarihi"
          value={patch.tarih.split("T")[0]}
          onChange={(e) => handlePatchChange("tarih", e.target.value)}
          className="w-full p-2 mb-1 border"
        />
        <TextField
          placeholder="Bölüm Başlığı"
          label="Bölüm Başlığı"
          value={patch.title}
          onChange={(e) => handlePatchChange("title", e.target.value)}
          className="w-full p-2 mb-1 border"
          required
        />
        <TextField
          placeholder="Alt Başlık"
          label="Alt Başlık"
          value={patch.subtitle}
          onChange={(e) => handlePatchChange("subtitle", e.target.value)}
          className="w-full p-2 mb-1 border"
        />

        <label className="label flex text-sm font-semibold text-start px-1">
          Yama Özeti
        </label>
        <textarea
          className="textarea w-full px-4 mb-4 border border-gray-700 focus-within:border-gray-300 focus:outline-none"
          placeholder="Yama Özeti"
          value={patch.summary}
          onChange={(e) => handlePatchChange("summary", e.target.value)}
        />

        <div className="mb-4">
          {patch.image ? (
            <div className="flex flex-col items-start gap-2">
              <img
                src={
                  patch.image.includes("uploads")
                    ? IMG_URL + patch.image
                    : patch.image
                }
                alt="Patch"
                className="max-h-40 rounded border"
              />
              <button
                type="button"
                onClick={() => handlePatchChange("image", "")}
                className="bg-red-600 text-white px-3 py-1 rounded text-sm"
              >
                Fotoğrafı Sil
              </button>
            </div>
          ) : (
            <ImageInput 
              label={"Yama Fotoğrafı"}
              onChange={(e) => handleImageUpload(e, 0, "image")}
              text="📂 Yama Fotoğrafı Yükle"
              required
            />
          )}
        </div>

        <h2 className="text-2xl font-semibold my-6">Bölümler</h2>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={patch.sections.map((s, i) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {patch.sections.map((section, i) => (
              <SortableSection
                key={section.id}
                id={section.id}
                title={section.title}
                index={i}
              />
            ))}
          </SortableContext>
        </DndContext>
        <div className="mt-12"></div>

        {patch.sections?.map((section, i) => (
          <div
            key={section.id}
            id={"sec" + section.id}
            className="mb-4 border p-2 rounded"
          >
            {section.bossUpdate ? (
              <BossUpdatesInput
                patch={patch}
                section={section}
                i={i}
                handleSectionChange={handleSectionChange}
                handleImageUpload={handleImageUpload}
                handleRowChange={handleRowChange}
                removeRow={removeRow}
                addColumn={addColumn}
                addRow={addRow}
                handleColumnChange={handleColumnChange}
                removeColumn={removeColumn}
                deleteTable={deleteTable}
                addTable={addTable}
                removeSection={removeSection}
              />
            ) : (
              <>
                <span className="flex mb-4 mx-1 font-bold">
                  {i + 1 + ". Bölüm"}
                </span>
                <TextField
                  placeholder="Bölüm Başlığı"
                  value={section.title}
                  onChange={(e) =>
                    handleSectionChange(i, "title", e.target.value)
                  }
                  className="w-full p-2 mb-1 border"
                />
                <div className="mt-4">
                  {section?.thumbnail && section?.thumbnail[0] ? (
                    <div className="flex flex-col items-start gap-2">
                      <img
                        src={
                          section.thumbnail[0]?.includes("uploads")
                            ? IMG_URL + section.thumbnail[0]
                            : section.thumbnail[0]
                        }
                        alt="Thumbnail"
                        className="max-h-32 rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => handleSectionChange(i, "thumbnail", [])}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Thumbnailı Sil
                      </button>
                    </div>
                  ) : (
                    <ImageInput
                      label={"Thumbnail Seç"}
                      type="file"
                      onChange={(e) => handleImageUpload(e, i, "thumbnail")}
                      text="📂 Thumbnail Yükle"
                      
                    />
                  )}
                </div>

                <EditableContent
                  patchNo={patch.yamaVersiyonu}
                  value={section.content}
                  onChange={(html) => handleSectionChange(i, "content", html)}
                  className="textarea w-full p-2 mb-2 border"
                />

                <div className="mt-4">
                  {section.images && section.images[0] ? (
                    <div className="flex flex-col items-start gap-2">
                      <img
                        alt={section?.title}
                        src={
                          section.images[0]?.includes("uploads")
                            ? IMG_URL + section.images[0]
                            : section.images[0]
                        }
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newSections = [...patch.sections];
                          newSections[i].images = [];
                          setPatch({ ...patch, sections: newSections });
                          setHasChange && setHasChange(true);
                        }}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm"
                      >
                        Fotoğrafı Sil
                      </button>
                    </div>
                  ) : (
                    <ImageInput
                      label={"Bölüm Resmi Seç"}
                      type="file"
                      text={"📂 Bölüm Fotoğrafı Yükle"}
                      onChange={(e) => handleImageUpload(e, i, "sections")}
                    />
                  )}
                </div>

                {section.table.columns.length > 0 && (
                  <div className="border p-2 mb-2 rounded">
                    <div className="flex gap-2 justify-between">
                      {section.table.columns.map((col, cIndex) => (
                        <div key={cIndex} className="flex items-center gap-1">
                          <TextField
                            placeholder={`Kolon ${cIndex + 1}`}
                            value={col}
                            onChange={(e) =>
                              handleColumnChange(i, cIndex, e.target.value)
                            }
                            className="p-2 border"
                          />
                          <button
                            type="button"
                            onClick={() => removeColumn(i, cIndex)}
                            className="bg-red-600 text-white rounded mb-4 p-1.5"
                          >
                            <MdDelete />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addColumn(i)}
                        className="bg-blue-600 text-white btn rounded"
                      >
                        <BsPlus className="text-white text-xl mt-0.5" /> Kolon
                        Ekle
                      </button>
                    </div>

                    {section.table.rows.map((row, rIndex) => (
                      <div key={rIndex} className="flex gap-2 mb-1">
                        {row.map((cell, cIndex) => (
                          <TextField
                            key={cIndex}
                            placeholder="Veri"
                            value={cell}
                            onChange={(e) =>
                              handleRowChange(i, rIndex, cIndex, e.target.value)
                            }
                            className="p-2 border"
                          />
                        ))}
                        <button
                          type="button"
                          onClick={() => removeRow(i, rIndex)}
                          className="bg-red-600 btn text-white flex rounded items-center px-3"
                        >
                          <MdDelete className="text-white" />
                          Satırı Sil
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addRow(i)}
                      className="bg-green-600 items-center text-white border-2 rounded flex mx-auto btn"
                    >
                      <BsPlus className="text-xl mt-0.5" /> Satır Ekle
                    </button>
                  </div>
                )}

                <div className="space-x-12 flex flex-col items-center">
                  <div className="space-x-4">
                    <button
                      type="button"
                      onClick={() =>
                        section.table.rows.length > 0
                          ? deleteTable(i)
                          : addTable(i)
                      }
                      className="btn bg-purple-600 text-white px-4 py-2 rounded mt-12 w-max mx-auto"
                    >
                      {section.table.rows.length > 0
                        ? "Tabloyu Sil"
                        : "Tablo Ekle"}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSection(i)}
                    className="bg-red-600 text-white px-4 py-2 mx-auto rounded mt-3 flex items-center gap-1 w-max self-end"
                  >
                    <MdDelete /> Bölümü Sil
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
        <div id={"lastSection"}></div>
        <div
          className={`-ml-7 right-5 py-2 mt-4 justify-around gap-6 fixed bottom-0 
            bg-gray-900 transition-opacity duration-300 hover:opacity-100 w-full z-50
            grid grid-cols-3 gap-x-4
            ${loading ? "opacity-100" : "opacity-0"}`}
        >
          <div className="flex gap-12 ml-12 justify-center">
            <button
              type="button"
              onClick={() => {
                addSection();
                setTimeout(() => {
                  document.getElementById("lastSection").scrollIntoView({
                    top: document.body.scrollHeight,
                    behavior: "smooth",
                  });
                }, 50);
              }}
              className="bg-amber-600 flex items-center text-white px-4 py-2 rounded"
            >
              <BsPlus className="text-xl mt-0.5" />
              Bölüm Ekle
            </button>
            <button
              type="button"
              onClick={() => {
                addSection(true);
                setTimeout(() => {
                  document.getElementById("lastSection").scrollIntoView({
                    top: document.body.scrollHeight,
                    behavior: "smooth",
                  });
                }, 50);
              }}
              className="bg-sky-600 flex items-center text-white px-4 py-2 rounded"
            >
              <BsPlus className="text-xl mt-0.5" />
              Boss Güncellemesi Ekle
            </button>
          </div>
          <button
            type="submit"
            className={`bg-green-600 text-white w-max justify-self-center text-lg px-8 py-2 rounded disabled:grayscale  ${
              loading && "grayscale"
            }`}
            onClick={() => setLoading(true)}
            disabled={!patch.yamaVersiyonu || !patch.image || !patch.title}
          >
            {loading ? "Kaydediliyor..." : "Kaydet"}
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="bg-red-600 text-white text-lg px-8 py-2 rounded w-max justify-self-center"
            >
              Sil
            </button>
          )}
        </div>
      </form>
      {showDeleteModal && onDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-sky-950 p-6 rounded shadow-lg w-96 text-white">
            <h2 className="text-xl font-bold mb-4">Emin misiniz?</h2>
            <p className="mb-6">Bu yama kalıcı olarak silinecek.</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 border rounded"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={() => {
                  onDelete(patch);
                  setShowDeleteModal(false);
                }}
                className="bg-red-600 text-white px-4 py-2 rounded"
              >
                {loading ? "Siliniyor..." : "Sil"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatchForm;
